// Callback.jsx - FIXED VERSION (waits for user role)
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGoogleDrive } from '../contexts/GoogleDriveContext';
import { useAuthState } from 'react-firebase-hooks/auth';
import { getFirebase } from '@services/firebase';

const { auth } = getFirebase();

const Callback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, userLoading] = useAuthState(auth);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [status, setStatus] = useState('Initializing...');
  const [waitingForUserRole, setWaitingForUserRole] = useState(true);
  const hasProcessed = useRef(false);
  const timeoutRef = useRef(null);
  const retryCountRef = useRef(0);
  const maxRetries = 10; // Wait up to 5 seconds (10 * 500ms)
  
  const { 
    processOAuthRedirect, 
    isLoading, 
    hasDriveAccess,
    userRole
  } = useGoogleDrive();

  // Cleanup function
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Wait for user role to load
  useEffect(() => {
    if (userRole !== '' && !userLoading) {
      setWaitingForUserRole(false);
      setStatus('User role loaded. Checking permissions...');
    } else if (userLoading) {
      setStatus('Loading user authentication...');
    } else {
      setStatus('Waiting for user role...');
    }
  }, [userRole, userLoading]);

  // Process OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const urlError = params.get('error');
    const state = params.get('state');
    
    // If we've already processed, don't do it again
    if (hasProcessed.current) {
      return;
    }

    // If still waiting for user role, check periodically
    if (waitingForUserRole) {
      if (retryCountRef.current < maxRetries) {
        timeoutRef.current = setTimeout(() => {
          retryCountRef.current += 1;
          setStatus(`Waiting for user role... (${retryCountRef.current}/${maxRetries})`);
          // Force re-check by setting state to trigger re-render
          setWaitingForUserRole(userRole === '' || userLoading);
        }, 500);
      } else {
        // Give up waiting after max retries
        setWaitingForUserRole(false);
        setError('Failed to load user permissions. Please refresh and try again.');
      }
      return;
    }

    // Check if we have a code or error
    if (!code && !urlError) {
      setStatus('Waiting for authorization code...');
      
      // Set a timeout for waiting too long
      timeoutRef.current = setTimeout(() => {
        setError('Authorization timed out. No code received.');
        setTimeout(() => {
          navigate('/enquiry', { 
            state: { error: 'Authorization timed out. Please try again.' } 
          });
        }, 2000);
      }, 30000);
      
      return;
    }

    // Mark as processed to prevent re-execution
    hasProcessed.current = true;
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Handle OAuth errors from Google
    if (urlError) {
      console.error('Google OAuth error:', urlError);
      
      let errorMessage = 'Authentication failed.';
      
      switch (urlError) {
        case 'access_denied':
          errorMessage = 'Access denied. You declined the authorization request.';
          break;
        case 'invalid_scope':
          errorMessage = 'Invalid permissions requested. Please contact support.';
          break;
        case 'server_error':
          errorMessage = 'Google authentication server error. Please try again.';
          break;
        default:
          errorMessage = `Authentication error: ${urlError}`;
      }
      
      setError(errorMessage);
      setStatus('Authentication failed');
      
      setTimeout(() => {
        navigate('/enquiry', { 
          state: { error: errorMessage } 
        });
      }, 3000);
      return;
    }

    // Validate state to prevent CSRF
    if (state) {
      try {
        const storedState = localStorage.getItem('oauth_state');
        
        if (!storedState) {
          console.warn('No stored OAuth state found');
        } else if (storedState !== state) {
          console.error('OAuth state mismatch - possible CSRF attack');
          setError('Security error: State mismatch. Please try again.');
          setStatus('Security check failed');
          
          setTimeout(() => {
            navigate('/enquiry', { 
              state: { error: 'Security error. Please try authenticating again.' } 
            });
          }, 3000);
          return;
        }
      } catch (err) {
        console.error('Error validating OAuth state:', err);
      }
    }

    // Parse redirect path from state or use default
    let redirectPath = '/enquiry';
    let searchTerm = '';
    
    if (state) {
      try {
        const stateObj = JSON.parse(decodeURIComponent(state));
        redirectPath = stateObj.redirectTo || '/enquiry';
        searchTerm = stateObj.searchTerm || '';
      } catch (err) {
        console.warn('Could not parse OAuth state, using defaults');
      }
    }

    // Clear stored state now that we've validated it
    localStorage.removeItem('oauth_state');
    localStorage.removeItem('oauth_attempt');

    // Process the authorization code
    const processAuthorization = async () => {
      setIsProcessing(true);
      
      try {
        // Check user permissions - now userRole should be loaded
        console.log('Checking drive access. User role:', userRole, 'hasDriveAccess:', hasDriveAccess());
        
        if (!hasDriveAccess()) {
          console.warn('User does not have Google Drive access permissions. Role:', userRole);
          setError(`Access denied. Your role (${userRole || 'Unknown'}) does not have permission to connect to Google Drive.`);
          setStatus('Permission denied');
          
          setTimeout(() => {
            navigate(redirectPath, { 
              state: { 
                error: `Access denied. Only Admin and Super users can connect to Google Drive. Your role: ${userRole || 'Unknown'}`,
                userRole 
              } 
            });
          }, 3000);
          return;
        }

        setStatus('Exchanging authorization code...');
        
        // Process the OAuth redirect
        const result = await processOAuthRedirect(code);
        
        if (result.success) {
          setStatus('Success! Redirecting...');
          
          // Clear URL parameters to prevent re-execution on refresh
          window.history.replaceState({}, document.title, window.location.pathname);
          
          setTimeout(() => {
            navigate(redirectPath, {
              state: {
                message: 'Successfully connected to Google Drive!',
                searchTerm,
                fromCallback: true,
                timestamp: new Date().toISOString()
              }
            });
          }, 1500);
        } else {
          throw new Error(result.error || 'Authentication failed');
        }
      } catch (err) {
        console.error('Error processing OAuth callback:', err);
        
        let userErrorMessage = 'Failed to authenticate with Google Drive.';
        
        // Provide user-friendly error messages
        if (err.message.includes('429')) {
          userErrorMessage = 'Too many authentication attempts. Please wait 15 minutes and try again.';
        } else if (err.message.includes('400')) {
          userErrorMessage = 'Invalid authorization code. The code may have expired. Please try again.';
        } else if (err.message.includes('network') || err.message.includes('Network')) {
          userErrorMessage = 'Network error. Please check your connection and try again.';
        } else if (err.message.includes('timeout')) {
          userErrorMessage = 'Authentication timed out. Please try again.';
        }
        
        setError(userErrorMessage);
        setStatus('Authentication failed');
        
        setTimeout(() => {
          navigate(redirectPath, { 
            state: { 
              error: userErrorMessage,
              fromCallback: true 
            } 
          });
        }, 3000);
      } finally {
        setIsProcessing(false);
      }
    };

    // Start processing
    processAuthorization();
  }, [
    location.search, 
    navigate, 
    hasDriveAccess, 
    userRole, 
    processOAuthRedirect,
    waitingForUserRole
  ]);

  // Show loading/status UI
  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      flexDirection: 'column',
      padding: '20px',
      textAlign: 'center',
      backgroundColor: '#f5f5f5'
    }}>
      <div style={{
        maxWidth: '500px',
        width: '100%',
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '40px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
      }}>
        <div style={{ marginBottom: '30px' }}>
          <div style={{
            width: '60px',
            height: '60px',
            margin: '0 auto 20px',
            backgroundColor: '#4285f4',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="white">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          </div>
          
          <h2 style={{ 
            margin: '0 0 10px 0', 
            color: '#333',
            fontSize: '24px'
          }}>
            Google Drive Authorization
          </h2>
          
          <p style={{ 
            color: '#666', 
            margin: '0 0 30px 0',
            fontSize: '16px'
          }}>
            {status}
          </p>
        </div>

        {/* Progress indicator */}
        <div style={{ 
          width: '100%', 
          height: '4px', 
          backgroundColor: '#e0e0e0',
          borderRadius: '2px',
          marginBottom: '20px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: waitingForUserRole ? '30%' : isProcessing ? '80%' : error ? '100%' : '60%',
            height: '100%',
            backgroundColor: waitingForUserRole ? '#ff9800' : error ? '#f44336' : isProcessing ? '#4285f4' : '#34a853',
            transition: 'width 0.3s ease',
            ...(waitingForUserRole && {
              animation: 'loading 1.5s ease-in-out infinite'
            })
          }}></div>
        </div>

        {/* Detailed status messages */}
        {waitingForUserRole && (
          <div style={{ 
            marginTop: '20px',
            padding: '15px',
            backgroundColor: '#fff3e0',
            borderRadius: '6px',
            borderLeft: '4px solid #ff9800'
          }}>
            <p style={{ 
              color: '#e65100', 
              margin: '0 0 10px 0',
              fontWeight: '500'
            }}>
              <strong>Loading User Permissions</strong>
            </p>
            <p style={{ 
              color: '#666', 
              margin: 0,
              fontSize: '14px'
            }}>
              Please wait while we load your user profile and permissions...
              {userLoading && ' (Authenticating user...)'}
              {!userLoading && userRole === '' && ' (Fetching user role...)'}
            </p>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: '8px',
              marginTop: '15px'
            }}>
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: '#ff9800',
                    animation: `bounce 1.4s infinite ${i * 0.16}s`
                  }}
                />
              ))}
            </div>
          </div>
        )}
        
        {isProcessing && !waitingForUserRole && (
          <div style={{ marginTop: '20px' }}>
            <p style={{ 
              color: '#4285f4', 
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <span style={{
                display: 'inline-block',
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                backgroundColor: '#4285f4',
                animation: 'pulse 1.5s infinite'
              }}></span>
              Processing authorization...
            </p>
          </div>
        )}
        
        {error && !waitingForUserRole && (
          <div style={{ 
            marginTop: '20px',
            padding: '15px',
            backgroundColor: '#ffebee',
            borderRadius: '6px',
            borderLeft: '4px solid #f44336'
          }}>
            <p style={{ 
              color: '#d32f2f', 
              margin: '0 0 10px 0',
              fontWeight: '500'
            }}>
              <strong>Error:</strong> {error}
            </p>
            <p style={{ 
              color: '#666', 
              margin: 0,
              fontSize: '14px'
            }}>
              You will be redirected shortly...
            </p>
          </div>
        )}

        <style>
          {`
            @keyframes loading {
              0% { transform: translateX(-100%); }
              100% { transform: translateX(100%); }
            }
            
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
            
            @keyframes bounce {
              0%, 80%, 100% { 
                transform: scale(0);
                opacity: 0.5;
              }
              40% { 
                transform: scale(1);
                opacity: 1;
              }
            }
          `}
        </style>
      </div>
      
      <div style={{
        marginTop: '20px',
        color: '#999',
        fontSize: '12px'
      }}>
        <p>This window will close automatically when authorization is complete.</p>
      </div>
    </div>
  );
};

export default Callback;